import { z } from 'zod';

import { renderPromptPlaceholders } from './common.js';
import { CreativeType } from './enums.js';

/**
 * VİDEO KREATİF SÖZLEŞMESİ — Genişleme Adım 7.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu dosya neden `creative.ts`ten AYRI
 * ═══════════════════════════════════════════════════════════════════════════
 * `creative.ts` TİPTEN BAĞIMSIZ olanı taşıyor: kütüphane satırı, iş takibi,
 * yükleme, publish. Video ise kendi eksenlerini getiriyor — süre, çözünürlük,
 * en-boy oranı, ses, konuşma metni, ses kimliği — ve bunların HİÇBİRİ görsel
 * ya da metin üretiminde bir karşılığı olan kavram değil. Aynı dosyaya
 * koymak, altı alandan dördü yalnız iki tipte anlamlı olan bir şema üretirdi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 SÜRE VE ÇÖZÜNÜRLÜK KREDİYİ DEĞİŞTİRİYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * Video, görsel üretiminden MERTEBE pahalı ve maliyeti sabit değil: sağlayıcı
 * SANİYE BAŞINA faturalıyor. Düz bir "video = 25 kredi" tarifesi 15 saniyelik
 * 1080p bir işte zararına satış demekti. Hesap `apps/api/src/creative/
 * creative.cost.ts`te (`videoCreditCost`); buradaki sabitler onun girdisi.
 *
 * Fiyatlandırmanın tek kaynağı yine `CREDIT_COST_BY_CREATIVE_TYPE`: oradaki
 * 25 / 40 değeri SİLİNMEDİ, anlamı netleşti — "varsayılan yapılandırmanın
 * fiyatı". Ölçek o tabandan çarpılarak çıkıyor, ikinci bir fiyat kaynağı
 * doğmuyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Sınırlar ÖLÇÜLDÜ, varsayılmadı
 * ═══════════════════════════════════════════════════════════════════════════
 * Süre aralığı, en-boy oranı kümesi ve ses bayrağı sağlayıcının canlı OpenAPI
 * şemasından okundu (13 Ağu 2026, `fal-ai/kling-video/v3/*`). Uydurulmuş tek
 * bir değer yok; sağlayıcı değişirse bu dosyadaki sabitler de ölçülerek
 * değişir.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Eksenler
// ═══════════════════════════════════════════════════════════════════════════

/**
 * En-boy oranı.
 *
 * `CREATIVE_FORMATS`ten AYRI ve bilerek DAR: orada `feed_portrait` (4:5) ve
 * `square_small` (600×600) da var, sağlayıcı ise yalnız bu üçünü kabul
 * ediyor. Görsel format listesini video için yeniden kullansaydık kullanıcı
 * 4:5 seçer, istek sağlayıcıya gider ve orada reddedilirdi — ya da daha
 * kötüsü, sessizce 9:16'ya yuvarlanıp beklediğinden başka bir video alırdı.
 *
 * Plan Adım 7'nin istediği üç oran (9:16 · 1:1 · 16:9) birebir bunlar.
 */
export const VIDEO_ASPECT_RATIOS = ['16:9', '9:16', '1:1'] as const;
export type VideoAspectRatio = (typeof VIDEO_ASPECT_RATIOS)[number];
export const videoAspectRatioSchema = z.enum(VIDEO_ASPECT_RATIOS);

/**
 * Çözünürlük.
 *
 * Seedance 1.5 Pro'da bu gerçek bir ALAN (`resolution`, enum 480p/720p/1080p)
 * — Kling'de ayrı uç noktalarıydı (`standard`/`pro`) ve eşleme adapter'ın
 * içinde saklanıyordu. Sağlayıcı değişimiyle eşleme gereksizleşti ama liste
 * BURADA KALDI: 480p'yi bilerek sunmuyoruz, reklam kreatifinde kabul
 * edilebilir alt sınır 720p.
 */
export const VIDEO_RESOLUTIONS = ['720p', '1080p'] as const;
export type VideoResolution = (typeof VIDEO_RESOLUTIONS)[number];
export const videoResolutionSchema = z.enum(VIDEO_RESOLUTIONS);

/**
 * Süre sınırları — sağlayıcının kabul ettiği aralık.
 *
 * `min`/`max` Seedance 1.5 Pro'nun `duration` enum'undan (4…12, tam saniye).
 * UGC videosunda süreyi model DEĞİL SES belirliyor (dudak senkronu ucu ses
 * dosyası kadar video üretiyor), o yüzden orada üst sınır ayrı: konuşma metni
 * uzadıkça video uzuyor ve `ugcMax` bunun tavanı.
 *
 * ⚠️ İKİ KEZ DEĞİŞTİ. 17 Ağu 2026'da 3…15'ten 4…12'ye daraldı (Kling v3 →
 * Seedance 1.5 Pro), 20 Ağu 2026'da Kling v3 Pro'ya dönülünce 3…15'e geri
 * açıldı. Genişleme kimseyi kırmıyor: daralmada kaybedilen değerler
 * (3 sn ve 13/14/15 sn) geri geliyor, mevcut hiçbir kayıt aralık dışına
 * düşmüyor.
 *
 * 🔴 Bu iki sayı sağlayıcının enum'unu YANSITIYOR, bizim tercihimiz değil.
 * Sağlayıcı değişirse ikisi birlikte değişir; `clampSceneDuration`
 * (`fal-video.provider.ts`) kuyrukta bekleyen eski işleri buraya sıkıştırıyor.
 */
export const VIDEO_DURATION = {
  min: 3,
  max: 15,
  /// PRODUCT_VIDEO varsayılanı — `CREDIT_COST.PRODUCT_VIDEO` (25 kredi) tam
  /// olarak bu yapılandırmanın fiyatı.
  productDefault: 8,
  /// UGC_VIDEO varsayılanı — `CREDIT_COST.UGC_VIDEO` (40 kredi) bunun fiyatı.
  ugcDefault: 15,
  /// Sesin sürdüğü kadar sürüyor; tavanı biz koyuyoruz.
  ugcMax: 30,
} as const;

/**
 * 🔴 KONUŞMA HIZI — UGC faturalamasının belkemiği.
 *
 * UGC videosunun süresi istekten değil ÜRETİLEN SESTEN çıkıyor. Kullanıcı
 * "5 saniye" deyip 600 karakterlik bir metin verirse ses ~40 saniye sürer;
 * sağlayıcı 40 saniyelik avatar videosu için faturalar, biz 5 saniyelik ücret
 * almış oluruz. Fark doğrudan zarar.
 *
 * Çözüm: metin verildiğinde süre ondan TÜRETİLİYOR (`creative.cost.ts` →
 * `billedVideoSeconds`) ve fatura büyük olan üzerinden kesiliyor. Kullanıcı
 * üretimden ÖNCE `/creatives/estimate` ile gerçek tutarı görüyor.
 *
 * 15 karakter/saniye: Türkçe için normal tempoda konuşma (~2,5 kelime/sn,
 * ortalama kelime uzunluğu ~6 karakter + boşluk). İngilizce biraz daha hızlı
 * ama tek bir sayı tutmak, dil başına iki farklı fatura üretmekten dürüst —
 * ve sapma her zaman BİZİM aleyhimize değil, kullanıcının lehine yuvarlanıyor
 * (aşağıdaki `min` sayesinde kısa metinde bile taban süre ücretlendiriliyor).
 */
export const SPEECH_CHARS_PER_SECOND = 15;

/// Konuşma metni üst sınırı. 30 sn × 15 karakter — `ugcMax` ile birebir
/// tutarlı; daha uzun metin kabul etmek, tavanı aşan bir videoyu kabul etmek
/// olurdu.
export const UGC_SCRIPT_MAX_CHARS = VIDEO_DURATION.ugcMax * SPEECH_CHARS_PER_SECOND;

/**
 * VİDEO brief'inin pratik tavanı — `CREATIVE_BRIEF_MAX`ten (6000) ayrı ve daha
 * dar, çünkü sınırı koyan şey burada şema değil SAĞLAYICI.
 *
 * Video sağlayıcısının `prompt` alanı 2500 karakterde bitiyor (ölçüldü,
 * `apps/worker/.../video-prompt.ts`) ve o alana yalnız brief değil şablonun
 * sahne yönergesi, marka tonu ve varyant notu da giriyor. Yani 2000'in ötesine
 * yazılan her karakter ya şablonun yönergesini iterdi ya da sonda sessizce
 * kırpılırdı — iki durumda da kullanıcı yazdığı şeyin uygulandığını sanır.
 *
 * Görselde böyle bir tavan YOK: orada brief doğrudan prompt oluyor ve sahne
 * şablonlarının reçeteleri tek başına 3000 karaktere çıkıyor.
 */
export const VIDEO_BRIEF_MAX_CHARS = 2000;

// ═══════════════════════════════════════════════════════════════════════════
// Ses
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Seslendirme kataloğu.
 *
 * Sağlayıcının ön tanımlı ses kimlikleri (MiniMax Speech). Serbest metin
 * KABUL EDİLMİYOR: tanınmayan bir `voice_id` sağlayıcıda 4xx doğuruyor ve o
 * hata TERMİNAL — kullanıcı kredisini yakıp "geçersiz ses" mesajı alırdı.
 * Kapalı liste, hatayı üretimden önce doğuruyor.
 *
 * `language` alanı ses kimliğinin dili DEĞİL, `language_boost` ipucu:
 * sağlayıcı sesleri çok dilli, ipucu telaffuzu düzeltiyor. Türkçe
 * sağlayıcının desteklediği diller arasında (ölçüldü, 13 Ağu 2026).
 */
export const VIDEO_VOICES = [
  { id: 'Wise_Woman', gender: 'female', tone: 'warm' },
  { id: 'Friendly_Person', gender: 'neutral', tone: 'friendly' },
  { id: 'Deep_Voice_Man', gender: 'male', tone: 'authoritative' },
  { id: 'Calm_Woman', gender: 'female', tone: 'calm' },
  { id: 'Casual_Guy', gender: 'male', tone: 'casual' },
  { id: 'Lively_Girl', gender: 'female', tone: 'energetic' },
] as const;
export type VideoVoiceId = (typeof VIDEO_VOICES)[number]['id'];
export const videoVoiceIdSchema = z.enum([
  'Wise_Woman',
  'Friendly_Person',
  'Deep_Voice_Man',
  'Calm_Woman',
  'Casual_Guy',
  'Lively_Girl',
]);

/// Seslendirme dili. `locale`den AYRI: kullanıcının arayüz dili ile reklamın
/// konuşma dili aynı olmak zorunda değil (Türk ekip İngilizce reklam çekiyor).
export const videoSpeechLanguageSchema = z.enum(['tr', 'en']);
export type VideoSpeechLanguage = z.infer<typeof videoSpeechLanguageSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Üretim parametreleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `POST /creatives/generate` gövdesindeki `video` nesnesi.
 *
 * Tamamı opsiyonel değil — `default()` taşıyor. Gerekçe: kredi hesabı bu
 * değerlerin HEPSİNİ istiyor ve "verilmediyse ne olacak" sorusunun cevabı
 * şemada durmalı. Servis katmanında `?? 8` gibi bir varsayılan yazmak, aynı
 * sorunun ikinci bir cevabını üretirdi ve ikisi bir gün ayrışırdı.
 */
export const videoParamsSchema = z.object({
  /// `VIDEO_TEMPLATES` kataloğundan. Yoksa brief'ten serbest üretim.
  templateId: z.string().trim().max(80).optional(),
  aspectRatio: videoAspectRatioSchema.default('9:16'),
  /**
   * 🔴 VARSAYILAN 1080p — 20 Ağu 2026'da 720p'den yükseltildi.
   *
   * 720p/1:1'de sağlayıcının gerçek çıktısı 960×960; reklam kreatifi için
   * yumuşak duruyor ve kullanıcı bunu ancak video bittikten sonra görüyordu.
   * Ham maliyet 8 saniyede $0,21 → $0,46, yani kreatif başına konulan $0,5
   * tavanının hâlâ altında.
   *
   * ⚠️ KREDİ TABANI DEĞİŞMEDİ: `CREDIT_COST.PRODUCT_VIDEO` (25) hâlâ
   * "8 sn 720p" yapılandırmasının fiyatı ve kart ızgarası "25 krediden
   * başlar" demeye devam ediyor — 720p seçilebilir olduğu sürece doğru.
   * Ama stüdyo formu artık 38 kredilik bir yapılandırmayla AÇILIYOR; taban
   * fiyatı varsayılanla eşitlemek istenirse değişmesi gereken yer
   * `creative.cost.ts` değil `CREDIT_COST_BY_CREATIVE_TYPE`.
   */
  resolution: videoResolutionSchema.default('1080p'),
  /**
   * İstenen süre (saniye).
   *
   * Üst sınır `ugcMax` (30), `max` (15) değil: aynı şema iki tipe hizmet
   * ediyor ve UGC'de süre daha uzun olabiliyor. Tipe özgü daralt­ma
   * `creative.cost.ts`te (`assertVideoParams`) — şemada yapılsaydı tek bir
   * `z.object` iki tip için iki farklı sınır taşımak zorunda kalırdı.
   */
  durationSeconds: z.number().int().min(VIDEO_DURATION.min).max(VIDEO_DURATION.ugcMax).optional(),
  /**
   * Sağlayıcının ürettiği ortam sesi/müzik (PRODUCT_VIDEO).
   *
   * 🔴 Varsayılan `false` ve bu BİLİNÇLİ: sağlayıcının kendi varsayılanı
   * `true` ve sesli üretim %50 daha pahalı. Alanı hiç göndermeseydik her
   * video sessizce 1,5 katına mal olurdu. Adapter bayrağı HER ZAMAN açıkça
   * gönderiyor.
   *
   * UGC'de anlamsız: ses zaten seslendirmeden geliyor.
   */
  withAudio: z.boolean().default(false),
  /// UGC seslendirmesi.
  voiceId: videoVoiceIdSchema.default('Friendly_Person'),
  language: videoSpeechLanguageSchema.default('tr'),
  /**
   * UGC konuşma metni.
   *
   * Verilmezse brief'ten ÜRETİLİYOR (Anthropic, `video-script.ts`). Verilirse
   * aynen okunuyor — kullanıcı kendi senaryosunu yazdıysa onu "iyileştirmek"
   * istediğinden başka bir şey söyletmek olurdu.
   */
  script: z.string().trim().max(UGC_SCRIPT_MAX_CHARS).optional(),
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * ÜRÜN KÜNYESİ — kullanıcının ELLE verdiği, fotoğraftan okunamayan bilgi
   * ═══════════════════════════════════════════════════════════════════════
   * Fotoğraf okuması (`productFactsSchema`) rengi, malzemeyi, silueti ve
   * ayırt edici detayları çıkarıyor — bunları modelin gözü kullanıcının
   * yazdığından daha iyi görüyor. Çıkaramadığı üç şey var ve üçü de çıktıyı
   * değiştiriyor:
   *
   *   · MARKA ADI — fotoğrafta okunaklı bir logo olmayabilir ve model
   *     olmayan bir markayı UYDURUYOR (ölçüldü: ürün görseli yokken önizleme
   *     tescilli bir logo bastı, `onizleme-gercek-marka-uyduruyor`).
   *   · KATEGORİ / ALT KATEGORİ — "çikolata barı" ile "protein barı" aynı
   *     fotoğrafa sahip olabilir ama aynı sahnede çekilmez.
   *   · AMAÇ — tanıtım mı indirim mi kullanım anlatımı mı. Kadraj aynı
   *     kalsa bile tempo ve kapanış değişiyor.
   *
   * Hepsi OPSİYONEL: künyesiz üretim çalışan bir yol ve zorunlu kılmak,
   * "hazır şablon" vaadini her seferinde dört kutu dolduran bir forma
   * çevirirdi.
   */
  product: z
    .object({
      /// Marka adı — prompt'a MARKA olarak giriyor, ekrana yazı olarak değil.
      brandName: z.string().trim().max(80).optional(),
      /// "Food", "Footwear", "Cosmetics" — sahne geleneğini belirliyor.
      category: z.string().trim().max(80).optional(),
      /// "Snacks, chocolate bar" — kategoriyi daraltıyor.
      subcategory: z.string().trim().max(120).optional(),
      /**
       * 🔴 LOGO KORUNSUN MU — bu üründeki en büyük SADAKAT kaldıracı.
       *
       * Varsayılan `true` ve gerekçesi şu: burada üretilen video
       * KULLANICININ KENDİ ürününün reklamı. Kendi markasının işaretini
       * silmek, reklamı markasız bırakmak demek.
       *
       * `false` yolu da gerçek bir kullanım: stok görselden çalışan,
       * tescilli bir işareti çoğaltmak istemeyen ya da kaynak fotoğrafta
       * başka bir markanın ürünü duran kullanıcı. O hâlde prompt her işareti
       * HARFSİZ soyut bir şekille değiştiriyor.
       *
       * ⚠️ `true` bir GARANTİ DEĞİL: video modelleri metni bozmakta ünlü ve
       * bunu prompt tek başına çözmüyor. Bayrağın yaptığı şey modele "bu
       * işaret korunacak" demek ve negatif listeye "warped logo" eklemek.
       */
      keepBranding: z.boolean().default(true),
      /// Videonun işi — tempo ve kapanışı belirliyor.
      goal: z.enum(['showcase', 'launch', 'promo', 'howto']).optional(),
    })
    .optional(),
});
export type VideoParams = z.infer<typeof videoParamsSchema>;
export type VideoProductBrief = NonNullable<VideoParams['product']>;
/// Şema uygulanmadan ÖNCEKİ hâl — `default()` alanları henüz dolmamış.
export type VideoParamsInput = z.input<typeof videoParamsSchema>;

/// Varsayılanı çözülmüş, tipe göre süresi yerine oturmuş parametre kümesi.
/// `videoParamsSchema` çıktısından farkı: `durationSeconds` artık kesin.
export interface ResolvedVideoParams extends Omit<VideoParams, 'durationSeconds'> {
  durationSeconds: number;
}

/// Video üreten kreatif tipleri. `CreativeType`i tek yerde süzüyoruz — üç
/// dosyada üç ayrı `type === PRODUCT_VIDEO || type === UGC_VIDEO` kontrolü
/// bir gün ayrışır.
export const VIDEO_CREATIVE_TYPES: readonly CreativeType[] = [
  CreativeType.PRODUCT_VIDEO,
  CreativeType.UGC_VIDEO,
];

export function isVideoCreativeType(type: CreativeType): boolean {
  return VIDEO_CREATIVE_TYPES.includes(type);
}

// ═══════════════════════════════════════════════════════════════════════════
// Şablonlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Şablon kategorisi — kullanıcının stüdyoda gördüğü sekmeler.
 *
 * `TEMPLATE_CATEGORIES` (otomasyon) ile karıştırılmamalı: o kural şablonları,
 * bu video reçeteleri. Aynı adı taşıyan iki kavram olmasın diye tip adı
 * `VideoTemplateCategory`.
 */
export const VIDEO_TEMPLATE_CATEGORIES = [
  /// Ürünün kendisi sahnede — tanıtım, detay, kullanım.
  'product',
  /// İndirim, kampanya, sezon sonu — aciliyet taşıyan reklam.
  'promo',
  /// Konuşan kişi: tanıklık, deneyim, öneri.
  'ugc',
  /// Marka hikâyesi, atmosfer, kuruluş anlatısı.
  'brand',
  /// Karşılaştırma ve kanıt: öncesi/sonrası, problem/çözüm.
  'proof',
] as const;
export type VideoTemplateCategory = (typeof VIDEO_TEMPLATE_CATEGORIES)[number];

/**
 * Şablon — prompt REÇETESİ, çıktının kendisi değil.
 *
 * ---------------------------------------------------------------------------
 * Neden `promptTemplate` bir dize ve yer tutucu taşıyor
 * ---------------------------------------------------------------------------
 * Alternatif, şablonu alan alan (sahne, kamera, ışık…) modellemekti. O yol
 * her yeni fikir için şemaya alan eklemek demekti ve şablonların yarısı
 * alanların yarısını boş bırakırdı. Dize + kapalı yer tutucu kümesi,
 * doğrulanabilir (aşağıdaki şema bilinmeyen yer tutucuyu reddediyor) ve
 * genişlemesi bedava.
 */
export const VIDEO_TEMPLATE_PLACEHOLDERS = [
  'brief',
  'brandTone',
  'brandColor',
  'industry',
] as const;
export type VideoTemplatePlaceholder = (typeof VIDEO_TEMPLATE_PLACEHOLDERS)[number];

export interface VideoTemplate {
  /// Okunur ve KALICI kimlik. Üretilmiş kreatifin `inputParams.video
  /// .templateId` alanı buna referans veriyor — değiştirmek, geçmiş işlerin
  /// kaynağını koparmak demek.
  id: string;
  category: VideoTemplateCategory;
  /// Hangi kreatif tipinde çalışır. UGC şablonu konuşma yönergesi taşıyor ve
  /// PRODUCT_VIDEO hattında karşılığı yok.
  type: Extract<CreativeType, 'PRODUCT_VIDEO' | 'UGC_VIDEO'>;
  /// Bu reçetenin anlamlı olduğu oranlar. Yatay bir "vitrin gezinmesi" 9:16'da
  /// çalışmaz; boş bırakmak yerine yazıyoruz ki arayüz seçeneği kapatabilsin.
  aspectRatios: readonly VideoAspectRatio[];
  /// Reçetenin tasarlandığı süre. Kullanıcı değiştirebilir; bu yalnız
  /// varsayılan.
  defaultDurationSeconds: number;
  /**
   * Kaynak görsel ZORUNLU mu.
   *
   * `true` ise şablon ürün fotoğrafından hareket üretiyor ve görselsiz
   * çalıştırılamaz. Arayüz bunu üretimden önce söylemek zorunda; sağlayıcıya
   * gidip "start_image_url eksik" hatası almak kredi yakar.
   */
  requiresSourceImage: boolean;
  /**
   * ÜRÜN FİLMİN BİR EVRESİNDE KADRAJDAN ÇIKIYOR MU.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 Bu bayrak bir ÜSLUP tercihi değil, bir ÇELİŞKİYİ kapatıyor
   * ═══════════════════════════════════════════════════════════════════════
   * Şablon yolunda prompt'un BAŞINA hattın kendi kimlik cümlesi ekleniyor
   * (`video-prompt.ts` → `identity`): "ürün HER ÇEKİMDE değişmeden görünür".
   * Katalogdaki 19 şablonun 18'i için doğru ve gerekli — ürünün kesmeler
   * arasında kaymasını engelleyen tek cümle o.
   *
   * Ama dönüşüm kurgusunda YANLIŞ: orada filmin bir evresi ürünü kadrajdan
   * çıkarıyor (yüzey başka bir şeye çözülüyor, sonra geri dönüyor). İki
   * cümle çelişince model en güvenli yolu seçiyor ve ürünü baştan sona
   * ekranda tutuyor — yani dönüşüm HİÇ OLMUYOR. Ölçüldü: 21 Ağu 2026'daki
   * ilk `product-pattern-morph` üretiminde kullanıcının bildirdiği kusur tam
   * olarak buydu.
   *
   * `true` olduğunda kimlik cümlesi ürünün AÇILIŞ ve KAPANIŞTA aynı kalmasını
   * istiyor, "her çekimde" demiyor. Kimlik kilidi kalkmıyor, kapsamı
   * daralıyor.
   *
   * ⚠️ Plan yolunun karşılığı `shotProductPresence` ve o çekim çekim
   * ölçüyor; burada çekim listesi yok, o yüzden şablonun kendi beyanı.
   */
  productAbsentPhase?: boolean;
  /// Sağlayıcıya giden yönerge. `{{brief}}` gibi yer tutucular çözülüyor.
  promptTemplate: string;
  /// İstenmeyenler. Sağlayıcının kendi varsayılanı var; şablon onu EZİYOR.
  negativePrompt?: string;
  /// UGC: konuşma metnini yazan modele verilen yönerge. `type: 'UGC_VIDEO'`
  /// olan her şablonda ZORUNLU (şema doğruluyor).
  scriptDirective?: string;
  /// Arayüz metinleri. Kaynak burada, çeviri `messages/*.json`da — otomasyon
  /// şablonlarıyla aynı desen ve aynı gerekçe.
  name: { tr: string; en: string };
  description: { tr: string; en: string };
}

/**
 * ŞABLON DOĞRULAMASI.
 *
 * 🔴 Katalog ELLE yazılmadı (ultracode ile üretildi) ve "ajan doğruladım
 * dedi" bir doğrulama değil. `video-templates.spec.ts` katalogdaki HER
 * kaydı bu şemadan tek tek geçiriyor — otomasyon şablonlarında
 * `validate-templates.ts` ne yapıyorsa aynısı.
 */
export const videoTemplateSchema = z
  .object({
    id: z
      .string()
      .min(3)
      .max(60)
      // Kimlik URL'de ve i18n anahtarında geçiyor; boşluk ve büyük harf
      // ikisinde de kaçış gerektirirdi.
      .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'kebab-case olmalı'),
    category: z.enum(VIDEO_TEMPLATE_CATEGORIES),
    type: z.enum([CreativeType.PRODUCT_VIDEO, CreativeType.UGC_VIDEO]),
    aspectRatios: z.array(videoAspectRatioSchema).min(1),
    defaultDurationSeconds: z.number().int().min(VIDEO_DURATION.min).max(VIDEO_DURATION.ugcMax),
    requiresSourceImage: z.boolean(),
    productAbsentPhase: z.boolean().optional(),
    promptTemplate: z.string().trim().min(40).max(1800),
    negativePrompt: z.string().trim().max(400).optional(),
    scriptDirective: z.string().trim().min(20).max(800).optional(),
    name: z.object({ tr: z.string().trim().min(2).max(60), en: z.string().trim().min(2).max(60) }),
    description: z.object({
      tr: z.string().trim().min(10).max(220),
      en: z.string().trim().min(10).max(220),
    }),
  })
  .superRefine((template, ctx) => {
    // UGC şablonu konuşma yönergesi taşımak ZORUNDA: taşımayan bir şablon
    // seçildiğinde metin üreten modele hiçbir yön verilmez ve avatar rastgele
    // bir şey söyler. Bunu çalışma zamanında fark etmek, kredi yakmak demek.
    if (template.type === CreativeType.UGC_VIDEO && !template.scriptDirective) {
      ctx.addIssue({
        code: 'custom',
        path: ['scriptDirective'],
        message: 'UGC şablonunda scriptDirective zorunlu',
      });
    }
    if (template.type === CreativeType.PRODUCT_VIDEO && template.scriptDirective) {
      ctx.addIssue({
        code: 'custom',
        path: ['scriptDirective'],
        message: 'PRODUCT_VIDEO şablonunda scriptDirective anlamsız — konuşan kimse yok',
      });
    }
    // UGC süresi ses tarafından sürülüyor; `max` (15) değil `ugcMax` (30)
    // geçerli. PRODUCT_VIDEO'da sağlayıcı 15 saniyeden uzununu kabul etmiyor.
    if (
      template.type === CreativeType.PRODUCT_VIDEO &&
      template.defaultDurationSeconds > VIDEO_DURATION.max
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['defaultDurationSeconds'],
        message: `PRODUCT_VIDEO en fazla ${VIDEO_DURATION.max} saniye`,
      });
    }
    // Bilinmeyen yer tutucu SESSİZ KALMAZ: `{{urun}}` yazan bir şablon
    // prompt'a olduğu gibi girer ve sağlayıcı o küme parantezini metin sanır.
    for (const match of template.promptTemplate.matchAll(/\{\{(\w+)\}\}/g)) {
      const key = match[1] as VideoTemplatePlaceholder;
      if (!VIDEO_TEMPLATE_PLACEHOLDERS.includes(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['promptTemplate'],
          message: `Bilinmeyen yer tutucu: {{${key}}}`,
        });
      }
    }
  });

/**
 * Yer tutucuları çözer.
 *
 * Boş değer için yer tutucu SATIRDAN SİLİNİYOR, boş dizeye çevrilmiyor:
 * "Brand tone: ." gibi bir kalıntı prompt'a girerse model onu bir yönerge
 * sanıyor. Aynı sebeple çözümden sonra fazla boşluklar tekleniyor.
 *
 * ⚠️ Gövde Genişleme Adım 8'de `common.ts`e (`renderPromptPlaceholders`)
 * TAŞINDI — davranış birebir aynı, `video-templates.spec.ts` bunu doğruluyor.
 * Sebep: banner kataloğu aynı kuralı istiyor ve iki kopya bir gün ayrışırdı.
 * İmza burada KALDI: çağıranların yer tutucu kümesini elle geçirmesi
 * gerekmesin.
 */
export function renderVideoPrompt(
  template: string,
  values: Partial<Record<VideoTemplatePlaceholder, string | null>>,
): string {
  return renderPromptPlaceholders(template, values, VIDEO_TEMPLATE_PLACEHOLDERS);
}

// ═══════════════════════════════════════════════════════════════════════════
// Arayüz sözleşmesi
// ═══════════════════════════════════════════════════════════════════════════

/// `GET /creatives/video/options` — stüdyo formunun tek kaynağı. Şablonlar,
/// sesler, sınırlar ve varsayılanlar tek çağrıda; arayüzün hiçbirini kendi
/// içinde kopyalaması gerekmiyor.
export interface VideoOptionsResponse {
  templates: {
    id: string;
    category: VideoTemplateCategory;
    type: CreativeType;
    aspectRatios: readonly VideoAspectRatio[];
    defaultDurationSeconds: number;
    requiresSourceImage: boolean;
    /// Arayüz çeviriyi `messages`tan okur; bu kaynak metin yedek.
    labelKey: string;
    descriptionKey: string;
    name: { tr: string; en: string };
    description: { tr: string; en: string };
  }[];
  voices: { id: string; gender: string; tone: string }[];
  aspectRatios: readonly VideoAspectRatio[];
  resolutions: readonly VideoResolution[];
  duration: {
    min: number;
    max: number;
    productDefault: number;
    ugcDefault: number;
    ugcMax: number;
  };
  /// Konuşma metni tavanı ve saniye tahmini — arayüz yazarken canlı süre
  /// gösterebilsin diye.
  script: { maxChars: number; charsPerSecond: number };
}

/**
 * Video maliyet dökümü — `POST /creatives/estimate` yanıtındaki `video` alanı.
 *
 * 🔴 SADECE TOPLAM DÖNMÜYOR, ÇARPANLAR DA DÖNÜYOR. Kullanıcı "neden 71 kredi"
 * diye sorduğunda cevabın arayüzde olması gerekiyor; yalnız toplam gönderip
 * gerekçeyi arayüzde yeniden hesaplatmak, ikinci bir fiyat mantığı yazmak
 * demekti.
 */
export interface VideoCostBreakdown {
  baseCredits: number;
  baselineSeconds: number;
  /// Faturalanan süre. Konuşma metni uzunsa istenen süreden BÜYÜK olabilir.
  billedSeconds: number;
  requestedSeconds: number;
  durationMultiplier: number;
  resolutionMultiplier: number;
  /// Metin uzunluğu yüzünden süre büyütüldüyse `true` — arayüz bunu açıkça
  /// söylemek zorunda, yoksa fiyat "kendiliğinden" artmış görünür.
  extendedByScript: boolean;
}
